import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { canonicalPathForVersion, VERSION_QUERY_KEY } from '@/shared/version-routing';
import { resolveVersion } from '@/versions/registry';

const PinnedVersionContext = createContext<string | null>(null);

/**
 * The version the reader is on: `?v=` when the URL has it, else the last one
 * it had. VersionSwitch mounts this, not the raw query, so a navigation that
 * drops `?v=` (a `<Navigate>`, a plain link) keeps the reader's version for
 * the render before the effect below puts `?v=` back. Reading the raw query
 * flipped the tree to the default version and back for that render, which
 * remounted the reader's version and loaded the default's chunk on the way.
 */
export function usePinnedVersion(): string | null {
  return useContext(PinnedVersionContext);
}

/**
 * Re-applies ?v= after in-app navigations that drop it, and remaps paths that
 * do not exist on the active MAJOR (e.g. v1 + /cases → /workbench).
 */
export function VersionQueryPreserver({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vParam = params.get(VERSION_QUERY_KEY);
  const pinnedV = useRef<string | null>(null);

  if (vParam) pinnedV.current = vParam;

  useEffect(() => {
    const effectiveId = resolveVersion(vParam ?? pinnedV.current).id;
    const canonical = canonicalPathForVersion(location.pathname, effectiveId);
    const needsPath = canonical !== location.pathname;
    const needsV = Boolean(pinnedV.current && !vParam);

    if (!needsPath && !needsV) return;

    const next = new URLSearchParams(location.search);
    if (pinnedV.current) next.set(VERSION_QUERY_KEY, pinnedV.current);

    navigate(
      { pathname: needsPath ? canonical : location.pathname, search: `?${next}` },
      { replace: true },
    );
  }, [location.pathname, location.search, vParam, navigate]);

  return <PinnedVersionContext.Provider value={vParam ?? pinnedV.current}>{children}</PinnedVersionContext.Provider>;
}
